<div class="container mx-auto max-w-3xl px-4 md:px-6 pt-8 pb-4">
  <!-- Header -->
  <div class="mb-6">
    <h2 class="text-2xl font-medium">Unit verplaatsen</h2>
    @if (currentUnit(); as u) {
      <p class="text-sm text-gray-500 mt-0.5">{{ u.name }}</p>
    }
  </div>

  @if (activeMove(); as active) {
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 mb-4 flex items-start gap-3">
      <mat-icon class="text-amber-600 shrink-0">info</mat-icon>
      <div class="flex-1">
        <p>
          Er is al een verplaatsing bezig. Wacht tot deze is afgerond voordat
          je een nieuwe verplaatsing start.
        </p>
        <button mat-button class="!mt-2" (click)="reCheckActiveMove()">
          Opnieuw controleren
        </button>
      </div>
    </div>
  }

  <div class="bg-white rounded-lg p-6 shadow-sm">
    <mat-stepper [linear]="true" (selectionChange)="onStepChange($event)">
      <!-- Always show step numbers instead of edit/done icons -->
      <ng-template matStepperIcon="edit" let-index="index">
        {{ index + 1 }}
      </ng-template>
      <ng-template matStepperIcon="done" let-index="index">
        {{ index + 1 }}
      </ng-template>

      <!-- Step 1: Target parent unit -->
      <mat-step [completed]="!!selectedTargetPrevId() && !!preview()">
        <ng-template matStepLabel>Nieuwe locatie</ng-template>

        <div class="py-4 space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p class="font-medium mb-1">Wat gebeurt er bij het verplaatsen?</p>
            <ul class="list-disc ml-4 space-y-0.5">
              <li>Templates met gedeelde media kunnen aanpassing nodig hebben</li>
              <li>Gebruikers binnen deze unit zien de pagina opnieuw laden</li>
              <li>Sommige gebruikers worden uitgelogd en teruggezet naar hun eigen unit</li>
              <li>De unit wordt tijdelijk vergrendeld tijdens het verplaatsen</li>
            </ul>
          </div>

          <mat-form-field class="w-full">
            <mat-label>Nieuwe bovenliggende unit</mat-label>
            <mat-select
              [ngModel]="selectedTargetPrevId()"
              (ngModelChange)="selectedTargetPrevId.set($event); onTargetSelected()"
              [disabled]="!!activeMove()"
            >
              @for (unit of availableParents(); track unit._id) {
                <mat-option [value]="unit._id">{{ unit.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (isLoadingPreview()) {
            <p class="text-sm text-gray-400">Voorbeeld laden...</p>
          }
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button (click)="cancel()">Annuleren</button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="!selectedTargetPrevId() || isLoadingPreview() || !preview() || !!activeMove()"
          >
            Volgende
          </button>
        </div>
      </mat-step>

      <!-- Step 2: Media resolution (skip if no broken media) -->
      <mat-step [completed]="maxStepReached() >= 1 && (!hasBrokenMedia() || isMediaStepValid())">
        <ng-template matStepLabel>Media</ng-template>

        <div class="py-4">
          @if (!hasBrokenMedia()) {
            <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
              <p>
                Er zijn geen gedeelde media die ontoegankelijk worden door deze
                verplaatsing. Je kunt deze stap overslaan.
              </p>
            </div>
          } @else {
            <p class="text-sm text-gray-600 mb-4">
              De volgende media worden gebruikt in templates binnen deze unit
              (of onderliggende units) maar zouden ontoegankelijk worden na de
              verplaatsing. Kies per item of de media <strong>gekopieerd</strong>
              of <strong>vervangen</strong> moet worden.
            </p>

            <div class="space-y-3">
              @for (row of mediaRows(); track row._id; let i = $index) {
                <div class="border rounded-lg p-4 space-y-3">
                  <div class="flex items-center gap-2">
                    <mat-icon class="text-gray-400">
                      {{ row.type === 'audio' ? 'audiotrack' : 'image' }}
                    </mat-icon>
                    <div class="flex-1">
                      <p class="font-medium text-sm">{{ row.label }}</p>
                      <p class="text-xs text-gray-500">
                        Gebruikt in {{ row.interactionCount }}
                        template{{ row.interactionCount === 1 ? '' : 's' }}
                        @if (row.interactionLabels.length) {
                          : {{ row.interactionLabels.join(', ') }}
                        }
                      </p>
                    </div>
                  </div>

                  <mat-radio-group
                    class="flex gap-4"
                    [ngModel]="row.action"
                    (ngModelChange)="onMediaActionChange(i, $event)"
                  >
                    <mat-radio-button value="copy">Kopiëren</mat-radio-button>
                    <mat-radio-button value="replace">Vervangen</mat-radio-button>
                  </mat-radio-group>

                  @if (row.action === 'replace') {
                    <div class="flex items-center gap-2">
                      <button
                        mat-stroked-button
                        type="button"
                        (click)="openMediaPicker(i)"
                      >
                        <mat-icon>image_search</mat-icon>
                        {{ row.targetMediaId ? 'Andere kiezen' : 'Media kiezen' }}
                      </button>
                      @if (row.targetMediaLabel) {
                        <span class="text-sm text-gray-700 truncate">
                          {{ row.targetMediaLabel }}
                        </span>
                      } @else {
                        <span class="text-sm text-gray-400">Geen media geselecteerd</span>
                      }
                    </div>
                  } @else {
                    <p class="text-xs text-gray-500">
                      Een kopie wordt opgeslagen in de mediabibliotheek van deze
                      unit. Als een eerdere kopie al bestaat, gebruiken we die
                      in plaats van een nieuwe.
                    </p>
                  }
                </div>
              }
            </div>
          }
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button matStepperPrevious>Terug</button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="hasBrokenMedia() && !isMediaStepValid()"
          >
            Volgende
          </button>
        </div>
      </mat-step>

      <!-- Step 3: User impact -->
      <mat-step>
        <ng-template matStepLabel>Gebruikers</ng-template>

        <div class="py-4 space-y-4">
          @if (preview(); as p) {
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 space-y-2">
              <p>
                Voor <strong>{{ p.usersToRefresh.count }}</strong> gebruiker{{ p.usersToRefresh.count === 1 ? '' : 's' }}
                @if (p.usersToRefresh.count > 0) {
                  ({{ p.usersToRefresh.sample.join(', ') }}@if (p.usersToRefresh.count > p.usersToRefresh.sample.length) { … })
                }
                zal de pagina opnieuw geladen worden zodra de verplaatsing
                klaar is. Hun openstaande wijzigingen kunnen verloren gaan.
              </p>
              <p>
                <strong>{{ p.usersToLogout.count }}</strong> daarvan worden
                <strong>uitgelogd</strong> en teruggezet naar hun eigen unit,
                omdat zij na de verplaatsing geen toegang meer hebben tot deze
                locatie. Zij moeten opnieuw inloggen.
              </p>
              <p class="text-xs">
                Deze verplaatsing kan enkele minuten duren.
              </p>
            </div>
          }
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button matStepperPrevious>Terug</button>
          <button mat-flat-button matStepperNext>Volgende</button>
        </div>
      </mat-step>

      <!-- Step 4: Summary & confirm -->
      <mat-step>
        <ng-template matStepLabel>Bevestigen</ng-template>

        <div class="py-4 space-y-4">
          <h3 class="font-medium text-lg">Samenvatting</h3>

          <div class="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Unit</span>
              <span class="font-medium">{{ currentUnit()?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Huidige bovenliggende unit</span>
              <span class="font-medium">{{ sourceParent()?.name ?? '—' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Nieuwe bovenliggende unit</span>
              <span class="font-medium">{{ selectedParent()?.name ?? '—' }}</span>
            </div>
            @if (hasBrokenMedia()) {
              <div class="flex justify-between">
                <span class="text-gray-500">Media gekopieerd</span>
                <span class="font-medium">{{ mediaCopyCount() }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Media vervangen</span>
                <span class="font-medium">{{ mediaReplaceCount() }}</span>
              </div>
            }
            @if (preview(); as p) {
              <div class="flex justify-between">
                <span class="text-gray-500">Gebruikers met pagina-herlaad</span>
                <span class="font-medium">{{ p.usersToRefresh.count }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Gebruikers uitgelogd</span>
                <span class="font-medium">{{ p.usersToLogout.count }}</span>
              </div>
            }
          </div>

          <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800">
            <p>
              <strong>Let op:</strong>
              Na bevestiging wordt deze unit vergrendeld en zijn instellingen
              niet meer aanpasbaar tot de verplaatsing klaar is.
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button matStepperPrevious>Terug</button>
          <button
            mat-flat-button
            color="primary"
            [disabled]="isSubmitting() || !!activeMove()"
            (click)="submit()"
          >
            @if (isSubmitting()) {
              Bezig met verplaatsen...
            } @else {
              Verplaatsen bevestigen
            }
          </button>
        </div>
      </mat-step>
    </mat-stepper>
  </div>
</div>
